import { useEffect, useState } from "react";
import styled from "styled-components";
import { Download } from "lucide-react";

import Panel from "../../../components/PartnerDashboard/Panel";
import Skeleton from "../../../components/PartnerDashboard/Skeleton";
import { Button, ErrorText, TwoCol } from "../../../components/PartnerDashboard/ui";
import { adminGetDropConversion } from "../../../api/apiEndpoints";
import { downloadCsv } from "../../../utils/reportDates";

interface Summary {
  people: number;
  attended: number;
  newToJr: number;
  newConverted: number;
  newJoinedDropStudio: number;
  avgDaysToPurchase: number | null;
  paidBefore: number;
  activeAtFirstDrop: number;
  lapsedAtFirstDrop: number;
  lapsedPaidAgain: number;
}

interface Person {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  firstDropAt: string;
  drops: number;
  attended: number;
  dropStudios: string | null;
  segment: "new" | "active" | "lapsed";
  purchasedAt: string | null;
  purchasedProduct: string | null;
  purchasedStudio: string | null;
}

const SEGMENT_LABEL = { new: "New to JackedRabbit", active: "Active member", lapsed: "Lapsed member" };

const PLAN_LABEL: Record<string, string> = {
  basic: "Trial",
  premium: "Upgrade (monthly)",
  premium_quarterly: "Upgrade (quarterly)",
  premium_yearly: "Upgrade (yearly)",
};

const Group = styled.div`
  font-family: 'Aileron', 'Poppins', sans-serif;
  background: #faf8f5;
  border: 1px solid #eee7dd;
  border-radius: 12px;
  padding: 16px 18px;
`;

const GroupTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #6b6259;
  margin-bottom: 4px;
`;

const Big = styled.div`
  font-size: 15px;
  color: #2a241f;
  margin-bottom: 10px;

  strong {
    font-size: 26px;
    font-weight: 800;
    color: #111111;
    margin-right: 6px;
  }
`;

const Line = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-top: 1px solid #eee7dd;
  font-size: 13.5px;
  color: #4a423b;

  span:last-child {
    font-weight: 700;
    color: #111111;
    text-align: right;
  }
`;

const isoDate = (v: string | null) => (v ? new Date(v).toISOString().slice(0, 10) : "");

interface Props {
  start: string;
  end: string;
  businessIds?: string;
  basis: "booked" | "class";
  invalid: boolean;
  fileSuffix: string;
}

const DropConversion = ({ start, end, businessIds, basis, invalid, fileSuffix }: Props) => {
  const [data, setData] = useState<{ summary: Summary; people: Person[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invalid) return;
    let cancelled = false;
    setData(null);
    setError(null);
    adminGetDropConversion({ start, end, businessIds, basis })
      .then((res) => !cancelled && setData(res.data))
      .catch(() => !cancelled && setError("Couldn't load class drop conversion. Please try again."));
    return () => {
      cancelled = true;
    };
  }, [start, end, businessIds, basis, invalid]);

  const download = () =>
    data &&
    downloadCsv(
      [
        ["First name", "Last name", "Email", "Type", "First drop booked", "Drops", "Attended", "Drop studios", "Bought after", "Bought on", "Bought at"],
        ...data.people.map((p) => [
          p.firstName,
          p.lastName,
          p.email,
          SEGMENT_LABEL[p.segment],
          isoDate(p.firstDropAt),
          p.drops,
          p.attended,
          p.dropStudios,
          p.purchasedProduct ? PLAN_LABEL[p.purchasedProduct] ?? p.purchasedProduct : "",
          isoDate(p.purchasedAt),
          p.purchasedStudio,
        ]),
      ],
      `Class drop conversion ${fileSuffix}.csv`,
    );

  const s = data?.summary;
  const of = (n: number, total: number) => `${n} of ${total}`;

  return (
    <Panel
      title="Class drop conversion"
      subtitle={s ? `${s.people} people used class drops · ${s.attended} attended at least one` : undefined}
      info={'"Paid before" and "bought after" are measured from when each person booked their first drop in this period. Buying after a drop doesn\'t prove the drop caused it, and recent drops haven\'t had much time to convert.'}
      action={
        <Button disabled={!data || data.people.length === 0} onClick={download}>
          <Download size={15} />
          CSV
        </Button>
      }
    >
      {error ? (
        <ErrorText>{error}</ErrorText>
      ) : !s ? (
        <Skeleton $h="180px" $radius="12px" />
      ) : (
        <TwoCol>
          <Group>
            <GroupTitle>New to JackedRabbit</GroupTitle>
            <Big>
              <strong>{s.newToJr}</strong>had never paid before their drop
            </Big>
            <Line><span>Bought a trial since</span><span>{of(s.newConverted, s.newToJr)}</span></Line>
            <Line><span>Joined a studio they dropped into</span><span>{s.newJoinedDropStudio}</span></Line>
            <Line>
              <span>Average days from drop to purchase</span>
              <span>{s.avgDaysToPurchase == null ? "—" : s.avgDaysToPurchase}</span>
            </Line>
          </Group>
          <Group>
            <GroupTitle>Already paid before</GroupTitle>
            <Big>
              <strong>{s.paidBefore}</strong>had paid for a membership before their drop
            </Big>
            <Line><span>Active member at the time</span><span>{s.activeAtFirstDrop}</span></Line>
            <Line><span>Lapsed member at the time</span><span>{s.lapsedAtFirstDrop}</span></Line>
            <Line><span>Lapsed members who paid again after</span><span>{of(s.lapsedPaidAgain, s.lapsedAtFirstDrop)}</span></Line>
          </Group>
        </TwoCol>
      )}
    </Panel>
  );
};

export default DropConversion;
